'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import AppStoreLink from '@/app/components/AppStoreLink'
import CorrectionNote from '@/app/components/blog/CorrectionNote'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Coffee: What's Really in Your Morning Cup?"
      author="MicroPlastics Team"
      date="April 10, 2025"
      readTime="9 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="MicroPlastics app helping check product safety"
    >
      <CorrectionNote date="September 21, 2026">
        This page previously said plastic pods release up to 16 billion nanoplastics per cup. No study has measured that; the figure comes from a study of plastic tea bags. It also cited a study of 13 food categories whose link led to an unrelated paper; that claim has been removed.
      </CorrectionNote>

      <BlogTLDR>
        <strong>The cup and the brewer matter more than the bean, and the
        measured numbers are smaller than the headlines.</strong> Hot coffee
        bought from UK coffee chains carried about 16 synthetic
        microplastics per cup, and capsule coffee 7 to 17 (Al-Mansoori et al.
        2025); lab tests that soak lined paper cups in hot water count up to
        25,000. The &ldquo;16 billion per cup&rdquo; pod figure comes from a
        study of plastic tea bags. Pour-over
        with a metal filter, French press, and stovetop moka are the cleanest
        common methods. Pods, plastic-lined paper cups, and plastic drip
        machines are the highest-exposure formats. Ceramic mug + metal-filter
        pour-over is the best single switch.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Plastic coffee pods (K-Cup style): 7–17 synthetic microplastics per cup in the one study of capsule coffee; nanoplastics from pods have never been counted.</>,
          <>Standard &ldquo;paper&rdquo; coffee cup: up to ~25,000 particles in a lab soak test (the PE liner is plastic); real coffee-shop coffee measured about 16 per cup.</>,
          <>French press + metal filter, stovetop moka, and pour-over with a metal mesh are the cleanest brewing methods.</>,
          <>Plastic kettles and plastic drip-machine reservoirs add particles before brewing even starts.</>,
          <>Single best swap: ceramic or stainless mug from home, not a takeout cup.</>,
          <>2026 update: exposure is driven by which plastic surface hot water touches and for how long, not by the bean.</>,
        ]}
      />

      <p>
        Coffee is the most popular beverage on Earth after water. Over <strong>2.25 billion cups</strong> are consumed every single day worldwide, and for hundreds of millions of people, the morning cup is a non-negotiable ritual. But a growing body of scientific research is revealing that your daily coffee may contain far more than caffeine and antioxidants. Depending on how you brew it, what container you drink from, and where your beans were stored, your coffee could be delivering a significant dose of microplastics with every sip.
      </p>
      <p>
        From plastic-lined paper cups to single-use pods, the modern coffee ecosystem is saturated with synthetic polymers. Understanding where these microplastics come from, how much you are actually ingesting, and which brewing methods minimize your exposure is essential for making informed choices about something you consume every day.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Coffee Gets Contaminated with Microplastics
      </h2>
      <p>
        Microplastics enter your coffee through multiple pathways, and the problem is more pervasive than most people realize. Unlike contamination from a single source, the plastic exposure from coffee is cumulative, it adds up from the water you use, the equipment you brew with, and the vessel you drink from.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Plastic Coffee Pods and Capsules
      </h3>
      <p>
        Single-serve coffee pods, popularized by Keurig K-Cups and Nespresso capsules, have become one of the most common brewing methods in North America and Europe. In the United States alone, over <strong>36 billion K-Cup pods</strong> have been sold since their introduction, and single-serve machines are now found in roughly one-third of American households. The convenience is undeniable, but the microplastic cost is staggering.
      </p>
      <p>
        Far less has been measured than is usually claimed. The one study that counted microplastics in capsule coffee (Al-Mansoori et al. 2025, <em>Science of the Total Environment</em>) found <strong>7 to 17 synthetic microplastics per cup</strong>, particles 10 µm and larger, across three home machines, with the oldest machine releasing the most. No study has counted nanoplastics from coffee pods; the &ldquo;billions per cup&rdquo; figure that circulates online comes from a 2019 McGill study of plastic tea bags. The mechanism is still worth taking seriously: near-boiling water forced through a small plastic container is the kind of contact that drives release, which is why a reusable steel pod is the lower-contact choice.
      </p>
      <p>
        Keurig K-Cups are primarily made from polypropylene (PP), a plastic that begins to degrade at temperatures above 100 degrees Celsius. While polypropylene is considered food-safe under normal conditions, the brewing process pushes water through the pod at approximately <strong>88 to 96 degrees Celsius</strong>, close enough to the degradation threshold to cause significant particle shedding, especially with repeated thermal stress on the pod material during manufacturing, shipping, and storage.
      </p>
      <p>
        Nespresso aluminum capsules contain a thin inner plastic lining to prevent the coffee from contacting the metal. While the particle counts from aluminum pods tend to be lower than from all-plastic pods, they are not zero. Research from the University of Barcelona found measurable microplastic release from aluminum coffee capsules, though at levels roughly <strong>10 to 100 times lower</strong> than from polypropylene pods.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Paper Cups with Plastic Linings
      </h3>
      <p>
        If you buy coffee from a cafe and drink it from a disposable paper cup, you are almost certainly consuming microplastics. The vast majority of takeaway coffee cups are not made from paper alone. They are lined with a thin layer of polyethylene (PE) plastic to make them waterproof. Without this plastic lining, the hot coffee would soak through the paper within minutes.
      </p>
      <p>
        A landmark 2021 study by researchers at the Indian Institute of Technology found that a single paper cup filled with hot liquid at 85 to 90 degrees Celsius releases approximately <strong>25,000 micron-sized microplastic particles</strong> into the beverage within 15 minutes. The study also detected the release of harmful ions including fluoride, chloride, sulfate, and nitrate from the plastic lining. Over the course of a year, someone drinking three takeaway coffees per day from paper cups would ingest an estimated <strong>27 million microplastic particles</strong> from the cups alone.
      </p>
      <p>
        A separate 2022 study from the National Institute of Standards and Technology (NIST) in the United States confirmed these findings, detecting trillions of nanoplastic particles released from single-use beverage cups when exposed to hot water. The NIST researchers noted that nanoparticles, being far smaller than microplastics, are especially concerning because they can cross cellular membranes and potentially accumulate in organs.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Plastic Kettles and Coffee Makers
      </h3>
      <p>
        The equipment you use to heat water also matters. Electric kettles with plastic interiors and drip coffee makers with plastic water reservoirs are both sources of microplastic contamination. A study published in <em>Environmental Pollution</em> found that boiling water in a plastic kettle releases an average of <strong>3 to 7 million microplastic particles per liter</strong> depending on the age of the kettle and the number of previous uses.
      </p>
      <p>
        Older kettles release more particles because the interior plastic surface degrades over time from repeated thermal cycling. Scratches, mineral deposits, and limescale buildup all accelerate the breakdown of the plastic surface. Standard drip coffee makers with plastic water tanks and tubing show similar contamination levels, with studies detecting polypropylene and polyethylene fragments in the brewed coffee.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Instant Coffee and Plastic Packaging
      </h3>
      <p>
        Even instant coffee is not immune. A 2022 study from researchers in Spain analyzed microplastic content across multiple instant coffee brands and found detectable levels of polyethylene, polypropylene, and polystyrene particles in the granules themselves. The contamination likely originates from plastic packaging materials, processing equipment with plastic components, and the industrial drying process. While the particle counts in instant coffee were lower than those from plastic pods or paper cups, they represent yet another additive source of exposure for daily coffee drinkers.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026 Update: It Is the Contact Surface, Not the Coffee
      </h2>
      <p>
        Two strands of 2026 evidence tighten the guidance on coffee, and both point the same
        way: the bean is not the problem, the vessel is.
      </p>
      <p>
        First, dietary context. Coffee is unlikely to be your largest source of
        microplastics; it is worth optimising because the fixes are easy, not
        because it dominates your intake.
      </p>
      <p>
        Second, and more directly useful, work from ANSES published in the{' '}
        <em>Journal of Food Composition and Analysis</em> demonstrated that in bottled drinks
        the contamination was arriving from the <strong>painted metal cap</strong>, not the
        container -- fine scratches in the cap paint shedding fragments into the drink. Glass
        bottles carried five to fifty times more fragments than plastic ones for exactly this
        reason.
      </p>
      <p>
        Applied to coffee, that principle is the whole story. What determines your exposure
        is which plastic surface hot water touches, and for how long: the pod, the PE-lined
        paper cup, the plastic brew basket, the reusable lid. Water at brew temperature
        against a polymer for several minutes is the worst case, and it is the case that
        describes most pod machines and every takeaway cup. Brewing into glass or steel, and
        decanting out of lined cups, addresses nearly all of it.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Which Brewing Methods Are Safest?
      </h2>
      <p>
        Brewing methods differ in how much microplastic they expose you to. The key factors are the materials that contact the hot water and coffee, the temperature involved, and the duration of contact. Here is a breakdown of common methods ranked from lowest to highest microplastic risk.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Pour-Over with Glass or Ceramic (Lowest Risk)
      </h3>
      <p>
        Pour-over methods like the Chemex, Hario V60, or a simple ceramic dripper with unbleached paper filters represent the gold standard for minimizing microplastic exposure. When you heat water in a stainless steel or glass kettle and pour it through a paper filter into a glass or ceramic vessel, <strong>no plastic touches the water at any point in the process</strong>. Unbleached paper filters are made from wood pulp without plastic coatings, and glass and ceramic are completely inert at brewing temperatures.
      </p>
      <p>
        If you use a pour-over method with metal filters (such as a gold-tone reusable filter), the risk remains very low as stainless steel does not shed microplastics. The only consideration is ensuring your kettle has a stainless steel or glass interior rather than plastic.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        French Press with Glass Carafe (Low Risk)
      </h3>
      <p>
        The classic French press uses a glass carafe, a stainless steel plunger, and a metal mesh filter. The hot water sits in contact with glass and metal only, making it an excellent low-plastic option. The one area to watch is the frame and lid, which on some models includes plastic components. However, since these parts are not submerged in the hot coffee for extended periods, the risk is minimal. Choose a French press with a stainless steel frame and lid for the absolute lowest exposure.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Moka Pot and Stovetop Espresso (Low Risk)
      </h3>
      <p>
        Traditional Italian moka pots are made entirely from aluminum or stainless steel. The water is heated in a metal chamber, forced through metal filter baskets, and collected in a metal upper chamber. There are no plastic components in contact with the hot water or coffee. Stainless steel moka pots are the preferred choice, as aluminum models, while plastic-free, may leach trace amounts of aluminum under acidic conditions.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Drip Coffee Makers (Moderate Risk)
      </h3>
      <p>
        Standard electric drip coffee makers typically have a plastic water reservoir, plastic tubing, and sometimes a plastic brew basket. Hot water passes through these plastic components before reaching your coffee grounds. While the contact time is relatively short, the repeated daily heating of the plastic reservoir contributes to cumulative particle release. If you prefer drip coffee, look for machines with <strong>stainless steel or glass water reservoirs and borosilicate glass carafes</strong>. Some premium brands like Technivorm Moccamaster use minimal plastic in their water pathways. Our{' '}
        <Link href="/blog/best-plastic-free-coffee-makers" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          plastic-free coffee maker
        </Link>{' '}
        ranking compares them.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Single-Serve Pods (Highest Risk)
      </h3>
      <p>
        As discussed above, single-serve plastic pods represent the highest microplastic risk of any common brewing method. The combination of small plastic containers, near-boiling water, and high-pressure brewing creates a worst-case scenario for polymer fragmentation. If you are committed to pod-based brewing for convenience, consider switching to <strong>compostable or paper-based pods</strong>, which are now available from several brands. Some companies offer reusable stainless steel pod inserts that can be filled with your own ground coffee, effectively eliminating the plastic from the equation.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Hidden Source: Your Water Supply
      </h2>
      <p>
        Before water even reaches your coffee maker, it may already contain microplastics. Studies have found that <strong>94% of tap water samples in the United States</strong> contain microplastic particles, with an average of 9.2 particles per liter. Bottled water is worse, with a 2024 Columbia University study detecting an average of <strong>240,000 nanoplastic fragments per liter</strong> of bottled water using advanced Raman spectroscopy.
      </p>
      <p>
        This means that even if you use a perfectly plastic-free brewing setup, the water itself contributes a baseline level of microplastic contamination. Installing a reverse osmosis (RO) filtration system or using an activated carbon block filter can remove up to <strong>99.9% of microplastic particles</strong> larger than 1 micron from your tap water, significantly reducing the total microplastic load in your coffee.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How Much Microplastic Are Coffee Drinkers Actually Ingesting?
      </h2>
      <p>
        The total microplastic exposure from coffee depends on how many cups you drink, your brewing method, your cup material, and your water source. Based on published research, here are estimated daily microplastic particle counts for common scenarios:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>2 cups from Keurig pods + paper cup:</strong> approximately 2.6 to 32 billion nanoplastics from pods, plus 50,000 microplastics from cups, one of the highest daily exposures from any food or beverage source.</li>
        <li><strong>2 cups from drip maker with plastic reservoir:</strong> approximately 6 to 14 million microplastic particles, primarily from the reservoir and tubing.</li>
        <li><strong>2 cups pour-over with glass/ceramic:</strong> exposure limited to particles already present in the tap water, typically fewer than 20 particles per cup.</li>
      </ul>
      <p>
        Over the course of a year, the difference between the highest-risk and lowest-risk brewing methods amounts to <strong>trillions of additional plastic particles</strong> ingested. Given that the long-term health effects of chronic microplastic ingestion are still being studied, minimizing exposure through simple equipment changes represents a sensible precautionary approach.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical Tips to Reduce Microplastics in Your Coffee
      </h2>
      <p>
        You do not need to give up coffee to dramatically reduce your microplastic exposure. Here are evidence-based steps you can take today:
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        1. Switch to a Glass or Stainless Steel Kettle
      </h3>
      <p>
        Replace any plastic kettle with one that has a stainless steel or borosilicate glass interior. This single change can eliminate millions of particles per liter from your hot water. Look for kettles that are certified plastic-free in the water pathway, as some &ldquo;stainless steel&rdquo; kettles still have plastic lids, spouts, or water level windows that contact the water.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        2. Ditch the Pods
      </h3>
      <p>
        If you currently use a Keurig, Nespresso, or similar pod machine, switching to any other brewing method will dramatically reduce your microplastic intake. If you love the convenience of pods, invest in a <strong>reusable stainless steel capsule</strong> that you fill with ground coffee. You will save money and eliminate billions of nanoplastic particles from your daily routine.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        3. Bring Your Own Cup
      </h3>
      <p>
        Carry a reusable ceramic, glass, or stainless steel travel mug when buying coffee. This eliminates the 25,000+ microplastic particles released from each paper cup. Many coffee shops offer discounts of 10 to 25 cents for bringing your own cup, so it saves money too. Avoid reusable plastic tumblers, which can also shed particles over time, especially when exposed to hot liquids.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        4. Filter Your Water
      </h3>
      <p>
        Use a reverse osmosis system, activated carbon block filter, or a gravity-fed ceramic filter to remove microplastics from your tap water before brewing. Simple pitcher-style filters like Brita are better than nothing but are less effective at removing the smallest particles compared to RO systems.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        5. Choose Your Brewing Method Wisely
      </h3>
      <p>
        Pour-over, French press, or moka pot brewing with non-plastic equipment gives you the lowest microplastic exposure. If you use a drip machine, look for models with stainless steel water paths and glass carafes. Avoid machines where hot water passes through extensive plastic tubing.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        6. Store Coffee in Glass or Metal Containers
      </h3>
      <p>
        Transfer your coffee beans or ground coffee from the original plastic bag to a glass jar with a metal lid or a stainless steel canister. Prolonged storage in plastic bags allows particles to transfer to the coffee, especially in warm environments. This also helps preserve freshness.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What About Plastic Straws and Lids?
      </h2>
      <p>
        Iced coffee drinkers face an additional source of exposure: plastic straws and lids. While the contact time is shorter than with hot beverages (since cold temperatures cause less plastic degradation), studies have still detected microplastic release from polypropylene lids and polystyrene straws. Switching to <strong>stainless steel or glass straws</strong> and using a reusable cup with a silicone or metal lid can help reduce this source of contamination.
      </p>
      <p>
        It is also worth noting that many iced coffee drinks from chain coffee shops are prepared in plastic cups, stored with ice in contact with the plastic, and served with plastic lids and straws. The cumulative plastic surface area in contact with your drink can be considerable.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Environmental Angle: Coffee Pods and Plastic Waste
      </h2>
      <p>
        Beyond personal health, the microplastic issue in coffee has enormous environmental implications. An estimated <strong>56 billion single-use coffee pods</strong> end up in landfills worldwide each year. Despite recycling programs offered by companies like Nespresso and Keurig, the actual recycling rate for coffee pods remains below 30% in most markets. In landfills, these plastic pods slowly break down over decades, releasing microplastics into soil and groundwater.
      </p>
      <p>
        The environmental contamination cycle is self-reinforcing: plastic waste breaks down into microplastics that contaminate water sources, which are then used to grow coffee crops and process coffee beans, introducing microplastics back into the product before it even reaches the consumer. Breaking this cycle requires both individual action and systemic change in how coffee is packaged and brewed.
      </p>

      {/* ------------------------------------------------------------------ */}
      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Frequently Asked Questions
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Do Keurig K-Cups contain microplastics?
      </h3>
      <p>
        Probably some. K-Cups are made from polypropylene, and hot pressurised water runs through them. The one study that counted microplastics in capsule coffee found <strong>7 to 17 synthetic microplastics per cup</strong> (Al-Mansoori et al. 2025), with the oldest machine releasing the most; it did not isolate the pod. The often-quoted &ldquo;1.3 to 16 billion&rdquo; figures do not come from any coffee study.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        How many microplastics are in a paper coffee cup?
      </h3>
      <p>
        Research from the Indian Institute of Technology found that a single paper coffee cup releases approximately <strong>25,000 micron-sized microplastic particles</strong> into hot liquid within 15 minutes. This is because paper cups are lined with polyethylene plastic to prevent leaking. Over a year of daily use, this adds up to millions of ingested particles.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        What is the safest way to brew coffee to avoid microplastics?
      </h3>
      <p>
        The safest method is a pour-over setup using a stainless steel or glass kettle, an unbleached paper filter, and a glass or ceramic mug. This ensures no plastic touches the hot water at any stage. French press with a glass carafe and stainless steel moka pots are also excellent low-plastic options.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Does cold brew coffee have fewer microplastics?
      </h3>
      <p>
        Cold brew can have fewer microplastics if prepared in glass containers, because the lower temperature causes less plastic degradation. However, if cold brew is stored in plastic containers or prepared in machines with plastic components, it can still accumulate microplastics. The key factor is the material of the container, not just the temperature.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">
        Are compostable coffee pods safer than plastic ones?
      </h3>
      <p>
        Compostable pods made from plant-based materials like PLA (polylactic acid) or paper release significantly fewer microplastic particles than conventional polypropylene pods. However, PLA can still shed some particles at high temperatures. The absolute safest pod alternative is a <strong>reusable stainless steel capsule</strong> filled with your own ground coffee.
      </p>

      {/* ------------------------------------------------------------------ */}
      <div className="bg-gradient-to-r from-[#1E6B47]/20 to-[#1E6B47]/5 rounded-xl p-6 mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          Check Your Products with the MicroPlastics App
        </h3>
        <p className="text-gray-600 mb-4">
          Scan any product to instantly see its microplastic risk level. Start with 3 free scans.
        </p>
        <AppStoreLink source="blog_post_inline" className="inline-flex items-center gap-2 bg-[#1E6B47] text-white font-semibold px-6 py-3 rounded-control hover:bg-[#f0a060] transition-colors pressable">Download Free on iOS</AppStoreLink>
      </div>

      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <p>
        For the head-to-head brewing comparison, pods vs French press vs
        espresso vs pour-over vs AeroPress, ranked by microplastic release,{' '}
        <Link href="/blog/microplastics-in-coffee-by-brewing-method" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          see the brewing-method ranking
        </Link>
        . For takeout cups, see{' '}
        <Link href="/blog/microplastics-in-coffee-cups" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          coffee cups: are they giving you microplastics?
        </Link>
      </p>

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Does coffee contain microplastics?',
            answer:
              'Yes. The microplastics in your cup come from three places: the brewing equipment (especially plastic pods and machine reservoirs), the takeout cup (PE-lined paper cups release ~25,000 particles per 15 minutes of hot contact), and the bean packaging or filter material. Beans themselves typically contribute very little.',
          },
          {
            question: 'What is the safest way to brew coffee for microplastics?',
            answer:
              'Pour-over with a metal mesh filter into a ceramic or stainless mug is the cleanest common method. French press, stovetop moka, and AeroPress (with a metal filter disc) are close. Avoid plastic pods, plastic drip machines with plastic reservoirs, and paper cup lids.',
          },
          {
            question: 'Do K-Cups and Nespresso pods release microplastics?',
            answer:
              'Probably a little. The one study that counted microplastics in capsule coffee found 7 to 17 synthetic microplastics per cup, with the oldest machine releasing the most. No study has counted nanoplastics from pods; the \u201c16 billion per cup\u201d figure comes from a study of plastic tea bags. Nespresso capsules are aluminum with a thin inner liner, so less plastic touches the water than in a K-Cup.',
          },
          {
            question: 'Are paper coffee cups plastic-free?',
            answer:
              'No. Standard paper takeout cups have a polyethylene (PE) plastic liner, without it, the cup would leak. A 2022 study estimated ~1,500 microplastic particles per hot drink. Compostable cups often use a PLA bioplastic liner that still sheds particles at brew temperature.',
          },
          {
            question: 'Does the coffee maker itself add microplastics?',
            answer:
              'Yes if it has plastic parts that contact hot water, most plastic-reservoir drip machines do. A stainless or glass percolator, French press, moka pot, or pour-over with a metal filter eliminates this source.',
          },
          {
            question: 'Should I quit coffee because of microplastics?',
            answer:
              'No. The exposure from coffee is meaningful but addressable. Switching brewing method (eliminate pods + paper cups) reduces it dramatically with no need to change the coffee itself. See the brewing-method ranking for the detailed comparison.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Al-Mansoori M, Harrad S, Abdallah MAE',
            year: 2025,
            title: 'Synthetic microplastics in hot and cold beverages from the UK market: comprehensive assessment of human exposure via total beverage intake',
            publisher: 'Science of the Total Environment',
            url: 'https://doi.org/10.1016/j.scitotenv.2025.180188',
          },
          {
            authors: 'ANSES (French Agency for Food, Environmental and Occupational Health Safety)',
            year: 2025,
            title: 'The caps of glass bottles contaminate beverages with microplastics',
            publisher: 'Journal of Food Composition and Analysis',
            url: 'https://www.anses.fr/en/content/caps-glass-bottles-contaminate-beverages-microplastics',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Assessing the Release of Microplastics and Nanoplastics from Plastic Containers and Reusable Food Pouches',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human Consumption of Microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
          {
            authors: 'Karami A, Golieskardi A, Choo CK, et al.',
            year: 2017,
            title: 'The presence of microplastics in commercial salts from different countries',
            publisher: 'Scientific Reports',
            url: 'https://www.nature.com/articles/srep46173',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
